/**
 * Guía: Ajustes del País.
 *
 * Contenido derivado del capítulo **17** del manual: §0 (el país es la unidad
 * raíz y su estado es una regla de negocio, no decoración), §5 (los siete
 * prerrequisitos de activación) y el flujo F28, que es como se le cuenta a
 * quien opera.
 *
 * El paso de la lista de verificación es el que evita la pregunta que más se
 * repite —«¿por qué no me deja activar?»—: los siete requisitos los comprueba
 * el servidor, no la pantalla.
 */

import type { TourFlow } from '../types';

const PAIS_PATH = '/country-config';

export const c17AjustesDelPais: TourFlow = {
  id: 'admin.country-config',
  chapter: '17',
  title: 'Ajustes del país',
  summary:
    'El centro de configuración de un país: los siete requisitos para activarlo y qué configura cada pestaña.',
  href: PAIS_PATH,
  requiresCountry: true,
  estimatedMin: 4,
  steps: [
    {
      id: 'intro',
      title: 'De aquí cuelga casi todo',
      body:
        'El país es la unidad raíz de la plataforma: empresas, clientes, billeteras, ' +
        'impuestos, tarifas, pasarelas, bancos y campañas dependen de él. Esta pantalla es ' +
        'su centro de configuración.',
      route: PAIS_PATH,
    },
    {
      id: 'summary',
      title: 'Lo que no se cambia desde aquí',
      body:
        'Código, prefijo telefónico, moneda e idioma. Se fijan al crear el país y ordenan ' +
        'todo lo demás — cambiar la moneda de un país con ventas reescribiría su historia.',
      target: 'admin.countryConfig.summary',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'inactive-by-default',
      title: 'Un país nace desactivado',
      body:
        'Y no es un interruptor: se activa cuando cumple sus siete requisitos, y el ' +
        'servidor los comprueba de verdad. Mientras esté desactivado, nada del país opera.',
    },
    {
      id: 'checklist',
      title: 'Los siete requisitos, y cuál falta',
      body:
        'Pasarela de cobro con credenciales activas, provincias, ciudades, impuestos, ' +
        'tarifa de servicio, entidad bancaria y proveedor de dispersión. La lista dice ' +
        'cuáles faltan y cada línea lleva a su pestaña. Desaparece al activarse: entonces ' +
        'sería ruido.',
      target: 'admin.countryConfig.checklist',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'manual-counts',
      title: 'La dispersión manual cuenta como requisito',
      body:
        'Un país puede activarse con dispersión manual, sin integración: alguien hace las ' +
        'transferencias por fuera y las marca. Es lo normal en un mercado recién abierto, y ' +
        'evita bloquear el arranque por una integración que aún no existe.',
    },
    {
      id: 'tabs',
      title: 'Siete pestañas, una por requisito',
      body:
        'Geografía, impuestos, tarifas, pasarelas, bancos, dispersión y Puntos 121. ' +
        'Ninguna tiene ruta propia: viven aquí dentro, así que se llega a ellas por esta ' +
        'pantalla y no por el menú.',
      target: 'admin.countryConfig.tabs',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'timezone',
      title: 'La zona horaria decide qué es «hoy»',
      body:
        'Todos los cierres, informes y paneles del país se resuelven en su zona horaria. ' +
        'Cambiarla reescribe qué entra en cada día en todas esas pantallas: no es un ajuste ' +
        'cosmético.',
    },
    {
      id: 'deactivate',
      title: 'Desactivar y borrar no son lo mismo',
      body:
        'Desactivar para el país y se puede revertir. Borrar está bloqueado en cuanto ' +
        'exista algo que cuelgue de él, que es casi siempre — y las dos acciones quedan ' +
        'registradas.',
      roles: ['SUPER_ADMIN'],
    },
  ],
};
